import { useCallback } from 'react';
import { batch, useDispatch } from 'react-redux';
import { change } from 'redux-form';

import AssetPreviewDisplay from 'dashboard/apps/InquiryTemplateEditor/ConnectedAssetPreviewDisplay';
import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { Asset, AssetType } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import { ToggleStyleInput } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
  SingleInputWrapper,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import { ThemeAttributes } from 'dashboard/models/theme';
import { NavbarLogoDisplay } from 'lib/interfaces/ThemeSerialized';

type NavBarLogoStylingSectionProps = {
  theme: ThemeAttributes;
  disabled: boolean;
};

const NavBarLogoStylingSection = ({ theme, disabled }: NavBarLogoStylingSectionProps) => {
  const dispatch = useDispatch();

  const onImageSelect = useCallback(
    (selectedAsset: Asset) => {
      /** should never happen - for type-checking */
      if (selectedAsset.type === AssetType.PERSONA) return;
      batch(() => {
        if (theme?.['logo-url'] == null) {
          dispatch(
            change(
              INQUIRY_TEMPLATE_EDITOR_FORM_NAME,
              'theme.navbar-logo-display',
              NavbarLogoDisplay.Center,
            ),
          );
        }

        // on save, we'll use the blob-id to fetch the asset and create a url
        dispatch(
          change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'theme.logo-filename', selectedAsset.filename),
        );
        dispatch(
          change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'theme.logo-data', selectedAsset['blob-id']),
        );

        // only for preview purpose
        dispatch(change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'theme.logo-url', selectedAsset.url));

        // remove unused attributes from store
        dispatch(change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'theme.delete-logo', undefined));
      });
    },
    [theme, dispatch],
  );

  const onRemoveNavbarLogo = () => {
    batch(() => {
      dispatch(change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'theme.delete-logo', true));
      // for preview purposes
      dispatch(change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'theme.logo-url', null));
      dispatch(change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, 'theme.logo-data', null));
    });
  };

  const onNavBarLogoAlignmentChange = useCallback(
    (value: string) => {
      dispatch(change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, `theme.navbar-logo-display`, value));
    },
    [dispatch],
  );

  return (
    <>
      <AssetPreviewDisplay
        assetInfo={theme?.['logo-data']}
        disabled={disabled}
        handleImageSelect={onImageSelect}
        handleRemoveAsset={onRemoveNavbarLogo}
        personaAssetsDisabled
      />
      {theme?.['logo-url'] != null && (
        <SingleInputWrapper>
          <SectionRow>
            <SectionColumn>
              <ToggleStyleInput
                name='navbar-logo-display-toggle'
                label='Alignment'
                field='navbar-logo-display'
                editable={!disabled}
                defaultValue={theme?.['navbar-logo-display']}
                options={[
                  { label: 'Left', value: NavbarLogoDisplay.Start },
                  { label: 'Center', value: NavbarLogoDisplay.Center },
                  { label: 'Right', value: NavbarLogoDisplay.End },
                ]}
                onChange={onNavBarLogoAlignmentChange}
              />
            </SectionColumn>
          </SectionRow>
        </SingleInputWrapper>
      )}
    </>
  );
};

export default NavBarLogoStylingSection;
